import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Observable, Subject, takeUntil } from 'rxjs';
import { BusinessLine } from 'src/app/shared/models/businessLine-model';
import { B2BCompanyConventionService } from '../b2b-company-convention.service';
import { errorMessage, lineOf } from '../catalog.model';
import { Convention, ConventionStatus, STATUS_LABELS } from '../convention.model';

function datesValidator(group: AbstractControl): ValidationErrors | null {
  const from = group.get('validFrom')?.value;
  const to = group.get('validTo')?.value;
  return from && to && to < from ? { dates: true } : null;
}

/** Dati generali della convenzione: stato, validità, monte ore e monte ore condiviso. Le regole restano invariate. */
@Component({
  selector: 'app-catalog-convention-modal',
  templateUrl: './catalog-convention-modal.component.html',
  styleUrls: ['./catalog-convention-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
})
export class CatalogConventionModalComponent implements OnInit, OnDestroy {
  @Input({ required: true }) companyId!: string;
  /** null = nuova convenzione. */
  @Input() convention: Convention | null = null;
  /** Linee dei servizi collegati all'azienda, tra cui scegliere quelle del monte ore condiviso. */
  @Input() lines: BusinessLine[] = [];
  @Output() saved = new EventEmitter<string>();
  @Output() closed = new EventEmitter<void>();

  readonly statuses = Object.keys(STATUS_LABELS) as ConventionStatus[];
  readonly statusLabels = STATUS_LABELS;
  readonly lineOf = lineOf;

  saving = false;
  formInvalid = false;
  confirmDelete = false;
  errorText = '';
  poolLines: BusinessLine[] = [];

  form = new UntypedFormGroup(
    {
      status: new UntypedFormControl('ACTIVE', [Validators.required]),
      validFrom: new UntypedFormControl(null, [Validators.required]),
      validTo: new UntypedFormControl(null, [Validators.required]),
      totalHours: new UntypedFormControl(null, [Validators.min(0)]),
      poolEnabled: new UntypedFormControl(false),
      poolHours: new UntypedFormControl(null, [Validators.min(0)]),
    },
    { validators: datesValidator }
  );

  private unsubscribe = new Subject<void>();

  constructor(private conventions: B2BCompanyConventionService) {}

  get isEdit() {
    return !!this.convention?.id;
  }

  get poolEnabled(): boolean {
    return !!this.form.get('poolEnabled')?.value;
  }

  /** Linee dei servizi più quelle già nel monte ore (anche se il servizio non è più collegato). */
  get lineOptions(): BusinessLine[] {
    return Array.from(new Set([...this.lines, ...this.poolLines]));
  }

  ngOnInit(): void {
    const c = this.convention;
    if (!c) return;
    this.form.patchValue({
      status: c.status,
      validFrom: c.validFrom ?? null,
      validTo: c.validTo ?? null,
      totalHours: c.totalHours || null,
      poolEnabled: !!c.sharedHoursPool?.enabled,
      poolHours: c.sharedHoursPool?.hours || null,
    });
    this.poolLines = [...(c.sharedHoursPool?.businessLines ?? [])];
  }

  togglePoolLine(line: BusinessLine) {
    this.poolLines = this.poolLines.includes(line) ? this.poolLines.filter((l) => l !== line) : [...this.poolLines, line];
  }

  submit() {
    this.errorText = '';
    if (this.form.invalid) {
      this.formInvalid = true;
      this.form.markAllAsTouched();
      return;
    }
    const v = this.form.value;
    const int = (x: unknown) => (x === null || x === undefined || x === '' ? null : Math.round(+x));
    const convention: Convention = {
      ...(this.convention ?? { rules: [] }),
      companyId: this.companyId,
      status: v.status,
      validFrom: v.validFrom || null,
      validTo: v.validTo || null,
      totalHours: int(v.totalHours),
      sharedHoursPool: { enabled: !!v.poolEnabled, hours: int(v.poolHours), businessLines: this.poolLines },
    };
    this.run(this.conventions.save(convention), this.isEdit ? 'Convenzione aggiornata' : 'Convenzione creata');
  }

  deleteConvention() {
    if (!this.convention?.id) return;
    if (!this.confirmDelete) {
      this.confirmDelete = true;
      return;
    }
    this.run(this.conventions.delete(this.convention.id), 'Convenzione eliminata');
  }

  private run(request: Observable<unknown>, message: string) {
    this.saving = true;
    request.pipe(takeUntil(this.unsubscribe)).subscribe({
      next: () => {
        this.saving = false;
        this.saved.emit(message);
      },
      error: (error) => {
        this.saving = false;
        this.confirmDelete = false;
        this.errorText = errorMessage(error, 'Impossibile salvare la convenzione.');
      },
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
